import { useTheme } from "@emotion/react";
import { Button } from "@mui/material";
import { useCallback, useEffect } from "react";
import { useParams } from "react-router-dom";
import { Lobby, LobbyDoorLink, LobbyDoorRoom, isLobbyDoorRoom } from "../config/config";
import { useConfig } from "../config/configContext";
import { Room } from "../config/parsedConfig";
import { useCurrentSession } from "../hooks/useCurrentSession";
import { getThemeColor } from "../utils/color";
import { convertSecondsToDurationString } from "../utils/time";
import NotFound from "./404";
import { DEFAULT_DOOR_IMAGE, UpcomingSessionInfo } from "./Room";

const onResize = () => {
    //find the hight of .lobby, and send it to the parent window
    const lobby = document.querySelector(".lobby");
    if (lobby) {
        const lobbyHeight = lobby.clientHeight;
        console.log("sending message to parent window: lobbyHeight=", lobbyHeight);
        window.parent.postMessage({ lobbyHeight }, '*');
    }
};

const LobbyDoorDisplay = ({ door, children }: { door: LobbyDoorLink, children?: React.ReactNode }) => {

    //register an event listener to resize window and load document events
    //this will cause the Lobby-height to change
    //and as this app is running in an iframe, we need to notify the parent window about the new height
    //so that the iframe can be resized accordingly
    //otherwise, parts of the Lobby might be cut off
    useEffect(() => {
        window.addEventListener('resize', onResize);
        window.addEventListener('load', onResize);
        return () => {
            window.removeEventListener('resize', onResize);
            window.removeEventListener('load', onResize);
        };
    }, []);

    const enterDoor = useCallback(() => {
        if (window.parent !== window) {
            //if this app is running in an iframe, send a message to the parent window to navigate to the room
            const msg = { navigateTo: door.url };
            console.log("sending message to parent window: ", msg);
            window.parent.postMessage(msg, '*');
        } else {
            //else, open a new tab with the room-url
            console.log("opening new tab: ", door.url);
            window.open(door.url, '_blank');
        }
    }, [door.url]);

    return <div className="room outside">
        <div className="room-head">
            <Button
                key="enter-room"
                variant='contained'
                color={door.color as any}
                size='large'
                fullWidth={true}
                onClick={enterDoor}
                sx={{
                }}
            >
                {door.name} &nbsp; ▶
            </Button>
        </div>
        <div className="room-image-container">
            <div className="layer img door"
                style={{ backgroundImage: `url(${door.doorImage})`, cursor: "pointer" }}
                onClick={enterDoor}
            >
            </div>
            {door.doorImageHover &&
                <div className="layer img door-open"
                    style={{ backgroundImage: `url(${door.doorImageHover})`, cursor: "pointer" }}
                    onClick={enterDoor}
                >
                </div>
            }
        </div>
        <div className="room-info">
            {children}
        </div>
    </div>;

}

const LobbyDoorRoomDisplay = ({ door, room }: { door: LobbyDoorRoom, room: Room }) => {
    const {
        currentSession,
        upcomingSession,
        upcomingSessionIsTomorrow,
        currentSessionRunningTime,
        timeUntilUpcomingSession,
    } = useCurrentSession(room);

    const theme = useTheme() as any;
    const roomColor = getThemeColor(theme, room.color || 'primary');

    const doorLink: LobbyDoorLink = {
        url: door.url,
        color: room.color || 'primary',
        doorImage: room.doorImage || DEFAULT_DOOR_IMAGE,
        doorImageHover: room.doorImageHover,
        name: room.displayName,
        info: ""
    };
    return <LobbyDoorDisplay door={doorLink} >
        {currentSession
            ? // display current session name, and time that is has been running for
            <div className="text-center">
                <p>
                    <span className="text-bold" style={{ color: roomColor }}>{currentSession.name}</span><br />
                    läuft seit <span className="text-bold">{convertSecondsToDurationString(currentSessionRunningTime)}</span>.
                </p>
            </div>
            : // display upcoming session: name, start time of day (local time! local time format!)
            <div className="text-center">
                <UpcomingSessionInfo
                    timeUntilUpcomingSession={timeUntilUpcomingSession}
                    upcomingSession={upcomingSession}
                    upcomingSessionIsTomorrow={upcomingSessionIsTomorrow}
                    roomColor={roomColor} />
            </div>
        }
    </LobbyDoorDisplay>
}

export const LobbyDisplay = ({ lobby }: { lobby: Lobby }) => {
    const { rooms } = useConfig();
    const theme = useTheme() as any;

    return (
        <div className="lobby">
            {lobby.doors.map((door, idx) => {
                if (isLobbyDoorRoom(door)) {
                    const room = rooms[door.roomName];
                    if (!room) {
                        return null;
                    }
                    return <LobbyDoorRoomDisplay key={idx} door={door} room={room} />;
                } else {
                    const color = getThemeColor(theme, door.color);
                    door = { ...door };
                    door.color = door.color || 'primary';
                    //door.info is coming from the config file.
                    //we want to support *bold* highlighting and newlines (rendered as <br/>) in the info text
                    //so we split the info text at the newline character, and render each line separately
                    const infoLines = door.info.split("\n");

                    return <LobbyDoorDisplay key={idx} door={door} >
                        <p className="text-center">
                            {infoLines.map((line, lineIdx) => {
                                //split the line at the * character, and render each part separately. don't split at "**"!
                                const parts = line.split("*");
                                return <>
                                    {(lineIdx > 0) && <br key={lineIdx} />}{/* add a line break after each line, except the first one */}
                                    {parts.map((part, idx) => {
                                        if (idx % 2 === 0) {
                                            return <span key={lineIdx+"_"+idx}>{part}</span>;
                                        } else {
                                            //support *#bold and colored* text. split at the # character and use it to determine whether the span should have door.color
                                            const style = part.startsWith("#") ? { color } : {};
                                            part = part.replace(/^#/, "");
                                            return <span key={lineIdx+"_"+idx} className="text-bold" style={style}>{part}</span>;
                                        }
                                    })}
                                </>;
                            })}
                        </p>
                    </LobbyDoorDisplay>;
                }
            })}
        </div>
    );
};

export const LobbyRoute = () => {
    const { lobbyName } = useParams();
    const lobbies = useConfig().lobbies;
    // lobby = lobbies[lobbyName]; fallback to lobbies.defaut; fallback to first lobby
    const lobby = lobbies[(lobbyName || "default")] || Object.values(lobbies)[0];
    if (!lobby) {
        return (
            <NotFound />
        );
    }
    return <LobbyDisplay lobby={lobby} />;
};

